<template>
  <div v-if="shouldShowBanner" class="cookie-consent-shell" role="dialog" aria-modal="true">
    <div class="cookie-consent-card shadow-lg">
      <div class="cookie-consent-title">{{ tOr('common.cookie_banner_title', 'Privacidad y cookies') }}</div>
      <p class="cookie-consent-copy mb-0">
        {{ tOr('common.cookie_banner_description', 'Usamos almacenamiento técnico para que la app funcione y solo te pedimos permiso para cargar contenido embebido de YouTube.') }}
      </p>

      <div class="cookie-consent-links">
        <router-link class="cookie-consent-link" to="/legal/privacy-policy">
          {{ tOr('common.privacy_policy_link', 'Política de privacidad') }}
        </router-link>
        <router-link class="cookie-consent-link" to="/legal/cookies-policy">
          {{ tOr('common.cookies_policy_link', 'Política de cookies') }}
        </router-link>
        <router-link class="cookie-consent-link" to="/legal/terms-conditions">
          {{ tOr('common.terms_conditions_link', 'Términos y condiciones') }}
        </router-link>
      </div>

      <div v-if="preferencesOpen" class="cookie-consent-settings">
        <div class="cookie-option-row">
          <div>
            <div class="cookie-option-title">{{ tOr('common.cookie_essential_title', 'Cookies técnicas necesarias') }}</div>
            <div class="cookie-option-text">{{ tOr('common.cookie_essential_description', 'Se usan siempre para autenticación, seguridad y funcionamiento interno de la plataforma.') }}</div>
          </div>
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" checked disabled>
          </div>
        </div>

        <div class="cookie-option-row">
          <div>
            <div class="cookie-option-title">{{ tOr('common.cookie_youtube_title', 'Contenido embebido de YouTube') }}</div>
            <div class="cookie-option-text">{{ tOr('common.cookie_youtube_description', 'Permite cargar vídeos embebidos y puede activar cookies de YouTube cuando reproduces contenido.') }}</div>
          </div>
          <div class="form-check form-switch">
            <input id="cookie-youtube-toggle" v-model="youtubeEnabled" class="form-check-input" type="checkbox">
          </div>
        </div>
      </div>

      <div class="cookie-consent-actions">
        <template v-if="preferencesOpen">
          <button type="button" class="btn btn-outline-secondary" @click="closePreferencesPanel">
            {{ tOr('common.cancel', 'Cancelar') }}
          </button>
          <button type="button" class="btn btn-primary" @click="savePreferencesSelection">
            {{ tOr('common.cookie_save_preferences', 'Guardar preferencias') }}
          </button>
        </template>
        <template v-else>
          <button type="button" class="btn btn-outline-secondary" @click="rejectOptional">
            {{ tOr('common.cookie_reject_optional', 'Rechazar opcionales') }}
          </button>
          <button type="button" class="btn btn-outline-primary" @click="openPreferencesPanel">
            {{ tOr('common.cookie_manage_preferences', 'Configurar') }}
          </button>
          <button type="button" class="btn btn-primary" @click="acceptAll">
            {{ tOr('common.cookie_accept_all', 'Aceptar todo') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

export default {
  name: 'CookieConsentBanner',
  data() {
    return {
      youtubeEnabled: false,
    };
  },
  computed: {
    ...mapState('cookieConsent', ['preferencesOpen']),
    ...mapGetters('cookieConsent', ['shouldShowBanner', 'currentPreferences']),
  },
  watch: {
    currentPreferences: {
      handler(preferences) {
        this.youtubeEnabled = !!preferences?.youtube;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    ...mapActions('cookieConsent', ['acceptAll', 'rejectOptional', 'savePreferences', 'openPreferences', 'closePreferences']),
    tOr(key, fallback) {
      return typeof this.$te === 'function' && this.$te(key) ? this.$t(key) : fallback;
    },
    openPreferencesPanel() {
      this.youtubeEnabled = !!this.currentPreferences?.youtube;
      this.openPreferences();
    },
    closePreferencesPanel() {
      this.youtubeEnabled = !!this.currentPreferences?.youtube;
      this.closePreferences();
    },
    savePreferencesSelection() {
      this.savePreferences({ youtube: this.youtubeEnabled });
    },
  },
};
</script>

<style scoped>
.cookie-consent-shell {
  position: fixed;
  left: 16px;
  right: 16px;
  bottom: 16px;
  z-index: 1200;
  display: flex;
  justify-content: center;
}

.cookie-consent-card {
  width: min(100%, 900px);
  background: rgba(255, 255, 255, 0.98);
  border: 1px solid rgba(15, 23, 42, 0.14);
  border-radius: 18px;
  padding: 1rem 1.1rem;
  backdrop-filter: blur(12px);
}

.cookie-consent-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: #0f172a;
}

.cookie-consent-copy {
  color: #475569;
  margin-top: 0.25rem;
}

.cookie-consent-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-top: 0.9rem;
}

.cookie-consent-link {
  color: #0f766e;
  font-size: 0.95rem;
  text-decoration: none;
}

.cookie-consent-link:hover {
  text-decoration: underline;
}

.cookie-consent-settings {
  margin-top: 1rem;
  display: grid;
  gap: 0.9rem;
}

.cookie-option-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0.95rem;
  background: #f8fafc;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.22);
}

.cookie-option-title {
  font-weight: 600;
  color: #0f172a;
}

.cookie-option-text {
  color: #475569;
  font-size: 0.92rem;
  margin-top: 0.2rem;
}

.cookie-consent-actions {
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

@media (max-width: 767.98px) {
  .cookie-consent-shell {
    left: 10px;
    right: 10px;
    bottom: 10px;
  }

  .cookie-option-row {
    flex-direction: column;
  }

  .cookie-consent-actions {
    justify-content: stretch;
  }

  .cookie-consent-actions .btn {
    width: 100%;
  }
}
</style>